<template>
  <div
    class="segment-card"
    :class="{ selected: props.isSelected }"
    @click="selectSegment">
    <div class="img">
      <img
        :src="segmentImage"
        :alt="demographic.name" />
    </div>
    <div
      class="hover"
      :style="!props.isSelected ? { background: segmentDetails?.color } : null">
      <p
        class="title"
        :style="{ borderColor: segmentDetails?.color || 'var(--primary)' }">
        {{ segmentName }}
      </p>
      <p
        v-if="props.isSelected && segmentDetails"
        class="description">
        {{ segmentDetails.socialGroup }} <br />
        {{ segmentDetails.lifeStage }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import { Demographic } from '@/common/models';
  import { PrizmSegmentDetailsModel } from '@/common/models/audience/PrizmSegmentDetails.model';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';

  // Props
  const props = defineProps({
    demographic: { type: Demographic, required: true },
    isSelected: { type: Boolean, required: false, default: false },
  });

  // Emit
  const emit = defineEmits<{ (e: 'segmentSelected', demographic: Demographic): void }>();

  // Cpmputed
  const segmentImage = computed<string>(() => AudienceSegmentUtils.getPrizmImage(props.demographic));
  const segmentName = computed<string>(() => AudienceSegmentUtils.getPrizmName(props.demographic));
  const segmentDetails = computed<PrizmSegmentDetailsModel>(() =>
    AudienceSegmentUtils.getPrizmDetails(props.demographic),
  );

  // Methods
  const selectSegment = (): void => {
    emit('segmentSelected', props.demographic);
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }

  .segment-card,
  .hover,
  img {
    height: 100%;
    width: 100%;
  }

  .segment-card {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;

    &:hover {
      .hover {
        transition: all 500ms ease;
        opacity: 0.9;
      }
    }

    &.selected {
      margin-bottom: 1.5rem;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: left;
      cursor: default;

      .img,
      .img img {
        width: 150px;
        height: 150px;
      }

      .img {
        display: block;
        margin-right: 1.5rem;

        img {
          object-fit: cover;
          object-position: center;
          position: relative;
          display: block;
          border-radius: 10%;
        }
      }

      .hover {
        background: none;
        display: flex;
        position: relative;
        justify-content: left;
        align-items: baseline;
        height: 13rem;
        width: 100%;
        flex-direction: column;
        overflow: hidden;
        opacity: 1;

        p {
          color: var(--textColor);
          text-align: left;
          padding-left: 0;
          margin-left: 1rem;

          &.title {
            padding-bottom: 0;
            margin-bottom: 1rem;
            margin-right: 1rem;
            border-bottom: 1px solid;
            width: 100%;
          }

          &.description {
            font-size: 1.4rem;
            font-weight: normal;
          }
        }
      }
    }

    .hover {
      background: var(--primaryColor);
      position: absolute;
      top: 0;
      left: 0;
      align-items: center;
      justify-content: center;
      transition: all 500ms ease;
      opacity: 0;
      display: flex;

      p {
        color: var(--primaryColorReverse);
        font-weight: bold;
        font-size: 2rem;
        text-align: center;
        padding: 1rem;
        margin: 0;
      }
    }
  }
</style>
